import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/app/auth';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  LoadingState,
  PageHeader,
} from '@/components/ui';
import { filterInputClass, formatNumber } from '@/utils/format';
import { keywordCsv, signalLabels } from './helpers';
import type { Brief, Keyword, Research } from './types';

export function KeywordResearchPage() {
  const { siteId = '' } = useParams();
  const { can } = useAuth();
  const client = useQueryClient();
  const [seedInput, setSeedInput] = useState('');
  const [seed, setSeed] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState('');
  const [tab, setTab] = useState('discover');
  const briefRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected)
      briefRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
      });
  }, [selected]);
  const path = `/sites/${siteId}`;
  const query = useQuery({
    queryKey: ['keyword-research', siteId, seed],
    queryFn: () =>
      api.get<Research>(`${path}/keyword-research`, {
        seed: seed || undefined,
      }),
    staleTime: 60_000,
  });
  const brief = useQuery({
    queryKey: ['content-brief', siteId, selected],
    queryFn: () =>
      api.get<Brief>(`${path}/content-brief`, { keyword: selected }),
    enabled: Boolean(selected),
  });
  const track = useMutation({
    mutationFn: (row: Pick<Keyword, 'keyword' | 'target_url'>) =>
      api.post(`${path}/tracked-keywords`, {
        keyword: row.keyword,
        target_url: row.target_url,
      }),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['keyword-research', siteId] }),
  });
  const untrack = useMutation({
    mutationFn: (id: string) => api.delete(`${path}/tracked-keywords/${id}`),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['keyword-research', siteId] }),
  });
  if (query.isLoading) return <LoadingState label="Reading keyword evidence" />;
  if (query.isError)
    return (
      <ErrorState
        message={query.error.message}
        onRetry={() => void query.refetch()}
      />
    );
  const data = query.data;
  if (!data) return null;
  const rows = data.keywords.filter(
    (row) =>
      row.keyword.toLowerCase().includes(search.toLowerCase()) &&
      (filter === 'all' ||
        row.signals.includes(filter) ||
        row.intent === filter),
  );
  const tracked = new Set(data.tracked.map((row) => row.keyword));
  const full =
    data.tracking_limit !== null && data.tracking_used >= data.tracking_limit;
  function exportCsv() {
    const url = URL.createObjectURL(
      new Blob(['\uFEFF' + keywordCsv(rows)], {
        type: 'text/csv;charset=utf-8',
      }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'keyword-research.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="space-y-5">
      <PageHeader
        title="Keyword research"
        description="Find the queries worth working on, map them to pages, and turn evidence into a content plan."
        actions={
          <>
            <Link className="btn-secondary" to={`${path}/roadmap`}>
              SEO roadmap
            </Link>
            <Button onClick={exportCsv}>Export CSV</Button>
          </>
        }
      />
      <div className="research-metrics grid grid-cols-2 gap-3 xl:grid-cols-4">
        {[
          ['Observed keywords', data.summary.observed_keywords],
          ['Striking distance', data.summary.quick_wins],
          ['Page overlap candidates', data.summary.overlap_candidates],
          ['Declining queries', data.summary.declining_keywords],
        ].map(([label, value]) => (
          <Card key={label}>
            <p className="text-xs text-ink-muted">{label}</p>
            <p className="mt-2 text-3xl font-semibold">
              {formatNumber(Number(value))}
            </p>
          </Card>
        ))}
      </div>
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-muted">
            {data.provenance.window_end
              ? `Search Console: ${data.provenance.window_start} – ${data.provenance.window_end}`
              : 'Connect Google Search Console to discover real query opportunities.'}{' '}
            · {data.provenance.pages_loaded} crawled pages
          </p>
          <Link className="text-sm text-brand" to={`${path}/search-setup`}>
            Google setup
          </Link>
        </div>
        <form
          className="mt-4 flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSeed(seedInput.trim());
          }}
        >
          <label className="sr-only" htmlFor="research-seed">
            Seed keyword
          </label>
          <input
            id="research-seed"
            value={seedInput}
            maxLength={512}
            onChange={(e) => setSeedInput(e.target.value)}
            placeholder="Explore a seed keyword, e.g. SEO services Kenya"
            className={`${filterInputClass} flex-1 min-w-60`}
          />
          <Button type="submit" loading={query.isFetching}>
            Explore keyword
          </Button>
        </form>
        <p className="mt-2 text-xs text-ink-muted">
          Seed keywords are ideas to validate. Impressions describe observed
          visibility; search volume and keyword difficulty are not available.
        </p>
      </Card>
      {selected ? (
        <div ref={briefRef} className="scroll-mt-5">
          <Card>
            <CardHeader
              title={`Content brief: ${selected}`}
              action={
                <Button size="sm" onClick={() => setSelected('')}>
                  Close brief
                </Button>
              }
            />
            {brief.isLoading ? (
              <LoadingState />
            ) : brief.isError ? (
              <p role="alert" className="text-danger">
                {brief.error.message}
              </p>
            ) : brief.data ? (
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Badge>{brief.data.intent}</Badge>
                  <p className="mt-3 text-sm text-ink-muted">
                    {brief.data.target_url
                      ? `Improve existing page: ${brief.data.target_url}`
                      : 'No mapped page. Validate demand and check existing coverage before creating one.'}
                  </p>
                  <h3 className="mt-4 text-sm font-semibold">
                    Suggested structure
                  </h3>
                  <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm">
                    {brief.data.suggested_sections.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ol>
                  {brief.data.related_keywords.length ? (
                    <p className="mt-4 text-sm text-ink-muted">
                      Related queries: {brief.data.related_keywords.join(', ')}
                    </p>
                  ) : null}
                </div>
                <div>
                  <h3 className="text-sm font-semibold">
                    Editorial acceptance checks
                  </h3>
                  <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-ink-muted">
                    {brief.data.editorial_checks.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs text-ink-muted">
                    {brief.data.notes}
                  </p>
                </div>
              </div>
            ) : null}
          </Card>
        </div>
      ) : null}
      <div className="flex gap-2" role="tablist" aria-label="Research views">
        {[
          ['discover', 'Discover'],
          ['clusters', 'Topic clusters'],
          ['watchlist', `Watchlist (${data.tracked.length})`],
        ].map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            className={`rounded-lg px-4 py-2 text-sm ${tab === key ? 'bg-ink text-white' : 'bg-white text-ink-muted border border-line'}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {track.isError || untrack.isError ? (
        <p role="alert" className="text-sm text-danger">
          {(track.error || untrack.error)?.message}
        </p>
      ) : null}
      {tab === 'discover' ? (
        <Card>
          <div className="mb-4 flex flex-wrap gap-3">
            <input
              aria-label="Filter keywords"
              className={`${filterInputClass} sm:w-64`}
              placeholder="Filter keywords"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              aria-label="Keyword opportunity filter"
              className={`${filterInputClass} sm:w-64`}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All keywords</option>
              {Object.entries(signalLabels).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
              {['informational', 'commercial', 'transactional'].map((i) => (
                <option key={i} value={i}>
                  {i} intent
                </option>
              ))}
            </select>
            <p className="ml-auto self-center text-xs text-ink-muted">
              Workspace watchlist: {data.tracking_used} /{' '}
              {data.tracking_limit ?? 'Unlimited'}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-line text-xs text-ink-muted">
                  {[
                    'Keyword / intent',
                    'Impressions',
                    'Clicks',
                    'Avg. position',
                    'Opportunity',
                    '',
                  ].map((h, i) => (
                    <th key={i} className="px-3 py-3 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.keyword}
                    className="border-b border-line/70 align-top"
                  >
                    <td className="max-w-80 px-3 py-4">
                      <button
                        className="text-left font-semibold text-brand"
                        onClick={() => setSelected(row.keyword)}
                      >
                        {row.keyword}
                      </button>
                      <p className="mt-1 text-xs text-ink-muted">
                        {row.intent} ·{' '}
                        {row.source === 'search_console'
                          ? 'Observed query'
                          : 'Seed idea'}
                      </p>
                      {row.target_url ? (
                        <p className="mt-2 break-all text-xs text-ink-muted">
                          {row.target_url}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-3 py-4">
                      {row.impressions === null
                        ? '—'
                        : formatNumber(row.impressions)}
                    </td>
                    <td className="px-3 py-4">
                      {row.clicks ?? '—'}
                      {row.click_delta !== null ? (
                        <p className="text-xs text-ink-muted">
                          {row.click_delta > 0 ? '+' : ''}
                          {row.click_delta} vs prior window
                        </p>
                      ) : null}
                    </td>
                    <td className="px-3 py-4">
                      {row.average_position?.toFixed(1) ?? '—'}
                    </td>
                    <td className="max-w-56 px-3 py-4">
                      <div className="flex flex-wrap gap-1">
                        {row.signals.map((s) => (
                          <Badge key={s}>{signalLabels[s] || s}</Badge>
                        ))}
                      </div>
                      {row.ranking_pages.length > 1 ? (
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer text-ink-muted">
                            {row.ranking_pages.length} observed pages
                          </summary>
                          {row.ranking_pages.map((p) => (
                            <p key={p.url} className="mt-2 break-all">
                              {p.url} · {p.impressions} impressions
                            </p>
                          ))}
                        </details>
                      ) : null}
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => setSelected(row.keyword)}
                        >
                          Brief
                        </Button>
                        {can('audit:run') ? (
                          <Button
                            size="sm"
                            disabled={
                              tracked.has(row.keyword) ||
                              full ||
                              track.isPending
                            }
                            onClick={() => track.mutate(row)}
                          >
                            {tracked.has(row.keyword) ? 'Tracked' : 'Track'}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length ? (
            <p className="py-8 text-center text-sm text-ink-muted">
              No keywords match. Explore a seed keyword, add target keywords in
              site settings, or sync Search Console.
            </p>
          ) : null}
        </Card>
      ) : null}
      {tab === 'clusters' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.clusters.map((cluster) => (
            <Card key={cluster.topic}>
              <CardHeader
                title={cluster.topic}
                description={`${cluster.intent} · ${formatNumber(cluster.observed_impressions)} observed page impressions`}
              />
              <div className="flex flex-wrap gap-2">
                {cluster.keywords.map((k) => (
                  <button
                    key={k}
                    onClick={() => setSelected(k)}
                    className="rounded-lg bg-canvas px-2 py-1 text-xs text-brand"
                  >
                    {k}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-ink-muted">
                Lexical grouping; review relevance and intent before combining
                pages.
              </p>
            </Card>
          ))}
          {!data.clusters.length ? (
            <p className="text-sm text-ink-muted">
              Explore a seed keyword to start a topic plan.
            </p>
          ) : null}
        </div>
      ) : null}
      {tab === 'watchlist' ? (
        <Card>
          <CardHeader
            title="Keyword watchlist"
            description="Live evidence from the latest synced Search Console window. Missing data means no matching query was observed in the loaded window."
          />
          {data.tracked.map((k) => (
            <div
              key={k.id}
              className="flex flex-wrap items-center gap-4 border-t border-line py-4"
            >
              <button
                onClick={() => setSelected(k.keyword)}
                className="mr-auto font-medium text-brand"
              >
                {k.keyword}
              </button>
              <span className="text-sm text-ink-muted">
                Avg. position {k.average_position?.toFixed(1) ?? '—'} ·{' '}
                {k.clicks ?? '—'} clicks
              </span>
              {can('audit:run') ? (
                <Button
                  size="sm"
                  disabled={untrack.isPending}
                  onClick={() => untrack.mutate(k.id)}
                >
                  Remove
                </Button>
              ) : null}
            </div>
          ))}
          {!data.tracked.length ? (
            <p className="text-sm text-ink-muted">
              Track keywords from Discover to monitor their observed
              performance.
            </p>
          ) : null}
        </Card>
      ) : null}

      <details className="text-xs text-ink-muted">
        <summary className="cursor-pointer">
          Data coverage and interpretation
        </summary>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {data.limitations.map((s) => (
            <li key={s}>{s}</li>
          ))}
          <li>
            Research loads up to 5,000 query rows and 2,000 pages; Discover
            shows the top 300 candidates.
          </li>
        </ul>
      </details>
    </div>
  );
}
